import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, ActivatedRoute, RouteReuseStrategy } from '@angular/router';
import { Subscription } from 'rxjs';

import { Abstract } from '../../../models/abstract/abstract.model';

import { AuthService } from '../../../services/auth.service';
import { AccountService } from '../../../services/account.service';
import { AbstractService } from '../../../services/abstract.service';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { Utilities } from '../../../services/utilities';
import { AbstractFormComponent } from './abstract-form/abstract-form.component';
import { ViewAbstractComponent } from './view-abstract/view-abstract.component';
import { CommonModule } from '@angular/common';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Roles } from '../../../models/enums';

@Component({
  selector: 'app-abstract-container',
  templateUrl: './abstract-container.component.html',
  styleUrls: ['./abstract-container.component.css'],
  imports: [
    AbstractFormComponent,
    CommonModule,
    ViewAbstractComponent
  ]
})

export class AbstractContainerComponent implements OnInit, OnDestroy {


  userName = '';
  fullName = '';
  subscription: Subscription = new Subscription();
  abstract: Abstract;
  isEdit: boolean;
  isCreate: boolean;
  title: string;
  mode: number;
  private _snackBar = inject(MatSnackBar);
  snackbarRef: MatSnackBarRef<any> | null;
  reconSupCoding: boolean = false;

  constructor(
    private abstractService: AbstractService,
    private accountService: AccountService,
    private alertService: AlertService,
    private authService: AuthService,
    private activeRoute: ActivatedRoute,
    /*private snackbarService: SnackbarService,*/
    public router: Router
  ) {
    // force route reload whenever params change;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit() {
    if (this.authService.isSessionExpired) {
      console.log(`ABSTRACT: REDIRECTING TO LOGIN`);
      this.router.navigateByUrl('/account/login');
      return;
    }
    this.userName = this.accountService.GetCurrentUserName();
    this.fullName = this.accountService.GetCurrentUserFullName();

    const url = this.activeRoute.snapshot.url;
    this.reconSupCoding = this.activeRoute.snapshot.queryParamMap.get('reconsupcoding') === 'true' ? true : false;

    this.isCreate = url[url.length - 1].path === 'create';

    /// [Notes:BJL_2025-08] In PACT 3.1, RecommendForReview (ReviewRequestedMode) and InReviewList (QCMode) will be one pool of abstracts for coding.
    /*this.mode = url[url.length - 1].path === 'viewcoderrequested' ? Utilities.ViewAbstractMode.ReviewRequestedMode : Utilities.ViewAbstractMode.QCMode;*/
    this.mode = Utilities.ViewAbstractMode.QCMode;

    this.title = this.isCreate ? 'CREATE ABSTRACT' : 'VIEW ABSTRACT';
 
    if (!this.isCreate) {
      this.getAbstractData();
    }
  }

  ngOnChanges() {
    this.reconSupCoding = this.activeRoute.snapshot.queryParamMap.get('reconsupcoding') === 'true' ? true : false;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    //if (this.snackbarRef != null) {
    //  this.snackbarRef.dismiss();
    //}
  }

  toggleViewEdit() {
    this.isEdit = false;// !this.isEdit;
    this.title = this.isEdit ? 'EDIT ABSTRACT' : 'VIEW ABSTRACT';
  }

  updateView(abstract: Abstract) {
    this.abstract = abstract;
    this.toggleViewEdit();
  }

  getAbstractData() {
    const abstractId = this.activeRoute.snapshot.params['id'];

    var actRole = this.accountService.GetActiveRole();
    var actProj = this.accountService.GetActiveProject();
    var actTeam = this.accountService.GetActiveTeam();

    // If user is a recon supervisor with no active team, redirect to team selection page
    if (actRole == Roles.ReconciliationSupervisor &&
    (!actTeam || actTeam.teamId == 0) && this.reconSupCoding) {
      this.router.navigate(['/account/team/select'], { replaceUrl: true });
    }
    else {
      const $abstract = abstractId
        ? this.abstractService.getRoleFilteredAbstractById(abstractId, this.accountService.GetActiveRole())
          : this.abstractService.getNextAbstract(this.accountService.GetActiveRole(), this.accountService.GetActiveTeam().teamId, this.accountService.GetActiveProject().odpProjectWorkflowId, this.mode);

      this.alertService.startLoadingMessage();


      this.subscription.add(
        $abstract.subscribe({
          next: abstract => {
            if (abstract.id == 0) {
              this.router.navigateByUrl("/home/actions");
              if (this.alertService.isLoadingInProgress) {
                this.alertService.stopLoadingMessage();
                /// [Notes:BJL_2025-08] In PACT 3.1, RecommendForReview (ReviewRequestedMode) and InReviewList (QCMode) will be one pool of abstracts for coding.
                if (this.accountService.GetActiveRole() == Roles.ReconciliationCoder || this.accountService.GetActiveRole() == Roles.ReconciliationSupervisor) {
                  /*this.alertService.showStickyMessage("View/Code Reconciliation Abstracts", `There are no abstracts available for reconciliation.`, MessageSeverity.warn);*/
                  this.openSnackBar('There are no abstracts available for reconciliation.', 'Dismiss');
                } else {
                  /*this.alertService.showStickyMessage("View/Code Abstract", `All enqueued abstracts for this team have been QC coded.`, MessageSeverity.warn);*/
                  this.openSnackBar('All enqueued abstracts for this team have been QC coded.', 'Dismiss');
                }


                //MessageSeverity.success
              }
            }
            else {
              this.abstract = abstract;

              this.abstract.groupIndex += 1;

              if (this.alertService.isLoadingInProgress) {
                this.alertService.stopLoadingMessage();
              }
            }
          },
          error: (error) => {
            if (this.alertService.isLoadingInProgress) {
              this.alertService.stopLoadingMessage();
            }

            const httpError = Utilities.getHttpResponseMessage(error);
            this.alertService.showStickyMessage(
              'Load Error',
              `Unable to retrieve abstract from the server.\r\nErrors: "${httpError}"`,
              MessageSeverity.error,
              error
            );
          }
        })
      );
    }
  }

  openSnackBar(message: string, action: string) {
    this.snackbarRef = this._snackBar.open(message, action, {
      panelClass: ['snack-warning'],
      verticalPosition: 'top',
      horizontalPosition: 'center',
      // duration: 15000 //in milliseconds or 10 seconds
    });
  }
}
